// Shared parsing for ztc-core's {code, message} error envelope. Mutation
// hooks call this on any non-2xx response so user- and admin-facing
// surfaces report the same backend failure with the same words.

export type ServerError = { code: string; message: string };

export const serverErrorMessage = async (res: Response, fallback: string): Promise<string> => {
  if (res.status === 401) return "Session expired. Refresh to sign in again.";
  const body = (await res.json().catch(() => null)) as ServerError | null;
  return body?.message ?? `${fallback} (${res.status})`;
};

/**
 * A response the CDN rewrote into the app shell.
 *
 * CloudFront maps BOTH 403 and 404 to `200 /index.html` so a deep link into
 * the SPA reloads instead of 404ing (ztc_frontend_cloudfront.tf). That rewrite
 * is distribution-wide - CloudFront has no way to exclude `/api/*` - so an API
 * call that legitimately 403s or 404s arrives at the browser as 200 with HTML
 * in it. `res.ok` is then TRUE, and the next line is usually `res.json()`,
 * which fails with `Unexpected token '<', "<!doctype "... is not valid JSON`.
 *
 * The customer saw that on the website sign-ins page, which is the surface
 * most likely to hit it: it answers 404 for an instance you do not manage, on
 * purpose, so existence is not leaked.
 *
 * The 200 is part of the test, not just the HTML: the rewrite always answers
 * 200, while an HTML body on a 502/503 is the origin or the load balancer
 * being down. Those keep their own status so the caller reports the outage
 * rather than telling the customer they lack access.
 */
export const isAppShell = (res: Response): boolean =>
  res.status === 200 && (res.headers.get("content-type") ?? "").toLowerCase().includes("text/html");

/* Says which of the two it was as far as it honestly can. The status is gone
 * by the time this runs - the CDN replaced it - so the message names both
 * possibilities rather than guessing one and being wrong half the time. */
export const APP_SHELL_MESSAGE =
  "That instance could not be found, or you do not have access to it. If you have just signed in, refresh and try again.";

/**
 * The JSON body, or a readable failure - never a parse error.
 *
 * Every caller that reads a JSON body should go through this rather than
 * `res.json()`, because a 200 is not a promise that the body is JSON.
 */
export const readJson = async <T>(res: Response, fallback: string): Promise<T> => {
  if (isAppShell(res)) throw new Error(APP_SHELL_MESSAGE);
  if (!res.ok) throw new Error(await serverErrorMessage(res, fallback));
  return (await res.json()) as T;
};
